// ===========================================================================
// PatientDetailDrawer — drawer lateral con el detalle del paciente.
//
// Fase 1 (MVP): renderiza con los datos que ya trae la fila de la lista
// (demografía, plan/network, PCP interno y última visita conocida). El
// histórico completo de visitas (TAB_VISIT_HISTORY) y el detalle ampliado por
// CIP son Fase 2 — el backend aún no expone esos SPs vía REST (ver
// ENDPOINTS_NO_RECENT_VISITS.md §11), por eso aquí solo se anticipa el espacio.
// ===========================================================================
import React from "react";
import { Drawer, Box, Typography, IconButton, Stack, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { SeverityBadge, MetalBadge } from "../shared/Badges";
import { formatLongDate, formatDaysAgo, safe } from "../../utils/format";
import { UNASSIGNED_LABEL } from "../../common/constants";

function Field({ label, children }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ py: 0.4 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right" }}>
        {children}
      </Typography>
    </Stack>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}
      >
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export default function PatientDetailDrawer({ patient, open, onClose }) {
  const p = patient || {};

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 440, maxWidth: "100%" } }}>
      <Box sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, pr: 1 }}>
            {p.memberFullName || "—"}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close detail">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          CIP: {safe(p.cipId)} · {safe(p.memberType)} · Member: {safe(p.memberId)}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <SeverityBadge bucket={p.timeSinceLastVisit} />
          {p.metalLevel && <MetalBadge level={p.metalLevel} />}
        </Stack>

        <Divider sx={{ mt: 2 }} />

        <Section title="Demographics">
          <Field label="DOB">
            {formatLongDate(p.dateOfBirth)}
            {p.age != null ? ` (${p.age} yrs)` : ""}
          </Field>
          <Field label="Gender">{safe(p.gender)}</Field>
          <Field label="Network">{safe(p.networkName)}</Field>
          <Field label="Plan">{p.metalLevel ? <MetalBadge level={p.metalLevel} /> : safe(null)}</Field>
        </Section>

        <Section title="Last Visit">
          <Field label="Date">
            {p.lastSeenDate ? formatLongDate(p.lastSeenDate) : "No record"}
          </Field>
          <Field label="Days since">{formatDaysAgo(p.daysSinceVisit)}</Field>
          <Field label="Time bucket">
            <SeverityBadge bucket={p.timeSinceLastVisit} />
          </Field>
        </Section>

        <Section title="Assigned PCP (internal)">
          <Field label="PCP">{p.pcpName || <span style={{ color: "#9ca3af" }}>{UNASSIGNED_LABEL}</span>}</Field>
          <Field label="Specialty">{safe(p.specialty)}</Field>
          <Field label="Location">{safe(p.medicalCenter)}</Field>
          <Field label="Office">{safe(p.officeLocation)}</Field>
        </Section>

        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Visit history is coming in a later phase.
        </Typography>
      </Box>
    </Drawer>
  );
}
